import {defineField} from 'sanity'

/**
 * AR-1800 — shared "Spacing" control for page blocks.
 *
 * Every block ships its own vertical padding, sized for landing pages. Stacked in
 * a page builder those paddings add up (two `richText` blocks = 40px + 40px), which
 * reads as a large unexplained gap inside an article. `spacing` lets an editor tune
 * an individual block; the frontend applies it in `Core/PageBuilder.vue`.
 */
export const blockSpacingField = defineField({
  name: 'spacing',
  title: 'Spacing',
  description:
    "Vertical space above and below this block. Automatic keeps the block's normal padding but removes the doubled-up gap where two blocks meet. Pick one of the others to set it yourself.",
  type: 'string',
  options: {
    layout: 'radio',
    direction: 'horizontal',
    list: [
      {title: 'Automatic', value: 'default'},
      {title: 'Compact', value: 'compact'},
      {title: 'None', value: 'none'},
      {title: 'Large', value: 'large'},
    ],
  },
  initialValue: 'default',
})

/**
 * Blocks the frontend can actually respace. `Core/PageBuilder.vue` changes the padding on
 * whichever element holds it — the block's root, or its single padded child — so a block
 * only qualifies if it has one such element. The rest spread padding across several
 * children or nest it deeper (galleryBox, hero, the specialEvent blocks, …); offering the
 * control there would show editors a setting that does nothing, so they are left out.
 *
 * Keep this in sync with ROOT_PADDED + INNER_PADDED in `Core/PageBuilder.vue`.
 */
const SPACING_SUPPORTED = [
  'availableWheelDesigns',
  'buttonList',
  'callToAction',
  'ctaGrid',
  'customAddToCart',
  'faqBlock',
  'featureList',
  'featuredContent',
  'mapEmbed',
  'registrationCta',
  'relatedArticles',
  'richText',
  'sizesPrices',
  'socialMedia',
  'stepGuide',
  'tableBlock',
  'team',
  'textWithImage',
  'transparentBoxes',
  'vehicleAlbum',
  'vehicleGallery',
  'vehicleSelector',
  'videoGrid',
  'wallpaper',
]

type SchemaWithFields = {
  name: string
  fields?: unknown[]
  [key: string]: unknown
}

/** Appends the shared Spacing field to a page-block schema. Idempotent; a no-op for
 * block types the frontend cannot respace. */
export const withBlockSpacing = <T extends SchemaWithFields>(schema: T): T => {
  if (!SPACING_SUPPORTED.includes(schema.name) || !Array.isArray(schema.fields)) {
    return schema
  }

  const alreadyHasSpacing = schema.fields.some(
    (field) => (field as {name?: string})?.name === 'spacing',
  )

  if (alreadyHasSpacing) {
    return schema
  }

  return {...schema, fields: [...schema.fields, blockSpacingField]} as T
}
